import React from "react";
import { Badge } from "@/components/ui/badge";
import { Target } from "lucide-react";
import { DeviceIcon } from "@/components/DeviceIcon";
import DashboardWidget from "@/components/DashboardWidget";

interface AnalyticsStats {
  trafficSources: {
    utm: Record<string, number>;
  };
  topDevicesBySource: Record<
    string,
    { type: string; os: string; count: number; percentage: number }
  >;
}

interface TopLinksWidgetProps {
  stats: AnalyticsStats | null;
}

export const TopLinksWidget: React.FC<TopLinksWidgetProps> = ({ stats }) => {
  const totalUTMVisitors = Object.values(
    stats?.trafficSources.utm || {},
  ).reduce((a, b) => a + b, 0);

  return (
    <DashboardWidget
      id="top-links"
      title="Top Performing Links"
      headerActions={<Target className="h-5 w-5 text-primary" />}
    >
      <div className="space-y-3">
        {Object.entries(stats?.trafficSources.utm || {})
          .sort(([, a], [, b]) => b - a)
          .slice(0, 5)
          .map(([source, count], index) => {
            // Source labels mapping
            const sourceLabels: Record<string, string> = {
              website: "Website Direct",
              facebook: "Facebook Organic",
              facebook_ads: "Facebook Ads",
              instagram: "Instagram Organic",
              instagram_ads: "Instagram Ads",
              tiktok_ads: "TikTok Ads",
              google_organic: "Google Organic",
              google_ads: "Google Ads",
              google_maps: "Google Maps",
              google_maps_ads: "Google Maps Ads",
              whatsapp: "WhatsApp Organic",
              whatsapp_ads: "WhatsApp Ads",
              business_card: "Business Card QR",
              poster: "Shop Poster QR",
              car_door: "Car Door Sign QR",
              window_sticker: "Car Window Sticker",
              flyer: "Flyers",
            };

            const label = sourceLabels[source] || source.replace("_", " ");
            const percentage =
              totalUTMVisitors > 0
                ? ((count / totalUTMVisitors) * 100).toFixed(1)
                : "0";
            const topDevice = stats?.topDevicesBySource?.[source];

            return (
              <div key={source} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="flex items-center justify-center w-6 h-6 bg-primary text-primary-foreground rounded-full text-xs font-bold">
                    {index + 1}
                  </span>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{label}</span>
                      {topDevice && (
                        <DeviceIcon
                          deviceType={
                            topDevice.type as "mobile" | "tablet" | "desktop"
                          }
                          os={topDevice.os}
                          className="h-3 w-3"
                        />
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {percentage}% of UTM traffic
                      {topDevice && (
                        <span className="ml-2">
                          • {topDevice.percentage.toFixed(0)}% {topDevice.os}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <Badge className="bg-gradient-to-r from-blue-500 to-purple-600 text-white">
                  {count}
                </Badge>
              </div>
            );
          }) || <p className="text-gray-500">No UTM link data available</p>}

        {totalUTMVisitors > 0 && (
          <div className="pt-3 border-t">
            <div className="text-sm text-gray-600 text-center">
              Total UTM Visitors:{" "}
              <span className="font-semibold">{totalUTMVisitors}</span>
            </div>
          </div>
        )}
      </div>
    </DashboardWidget>
  );
};

export default TopLinksWidget;
